import { CallbackWith, ErrorHandlerCallback, Maybe } from '@csp/csp-common-model';
import { noop } from 'lodash';
import { useCallback, useState } from 'react';
import { LoadingState } from './model/LoadingState';
import { ErrorInfoArgs } from './model/ErrorInfoArgs';
import { useBoolean } from './useBoolean';
import { useErrorInfo } from './useErrorInfo';

type Data<T> = { data: T };
type ErrorOrData<T> = ErrorInfoArgs | Data<T>;

const isErrorArg = (errorOrData: Maybe<ErrorOrData<unknown>>): errorOrData is ErrorInfoArgs =>
  !!errorOrData && (errorOrData as ErrorInfoArgs).error !== undefined;

export type UseLoadingReturnType<T> = LoadingState<T> & {
  setLoading(): void;
  clearLoading(errorOrData?: ErrorOrData<T>): void;
  clearError(): void;
};

type Params = {
  isLoadingInitial?: boolean;
  getErrorMessage?: CallbackWith<Error, string>;
  errorHandler?: ErrorHandlerCallback;
  clearErrorOnSetLoading?: boolean;
};

const pickMessage: CallbackWith<Error, string> = error => error.message;

export const useLoading = <T>(params?: Params): UseLoadingReturnType<T> => {
  const { getErrorMessage = pickMessage, errorHandler = noop, clearErrorOnSetLoading = false } = params ?? {};
  const [data, setData] = useState<T>();
  const { errorInfo, clearErrorInfo, setErrorInfo } = useErrorInfo({ getErrorMessage, errorHandler });
  const [isLoading, setIsLoading, setNotLoading] = useBoolean(params?.isLoadingInitial ?? false);

  const clearLoading = useCallback(
    (errorOrData?: ErrorOrData<T>): void => {
      if (isErrorArg(errorOrData)) {
        setErrorInfo(errorOrData);
      } else {
        clearErrorInfo();
        setData(errorOrData?.data);
      }
      setNotLoading();
    },
    [clearErrorInfo, setErrorInfo, setNotLoading],
  );

  const setLoading = useCallback((): void => {
    if (clearErrorOnSetLoading) {
      clearErrorInfo();
    }
    setIsLoading();
  }, [clearErrorInfo, clearErrorOnSetLoading, setIsLoading]);

  return {
    clearLoading,
    setLoading,
    isLoading,
    data,
    error: errorInfo,
    clearError: clearErrorInfo,
  };
};
